// import { yupResolver } from '@hookform/resolvers/yup';
import {
  TextareaAutosize,
  FormHelperText,
  Stack,
  Divider,
  Grid,
  Box,
  styled,
  CircularProgress,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { FormProvider, useForm, FieldValues, Controller } from 'react-hook-form';
import {
  IJob,
  ICandidate,
  getJobByJobCode,
  JobAssignmentStatusFilter,
  getJobsFromAdvancedQuery,
  ICandidateResume,
  IMappingFormSettingsResponse,
  PipelineResultCommSettings,
} from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  Item,
  IConversationState,
  WsButton,
  WsColors,
  WsTagsList,
  ChipsType,
} from 'wisestep-base';

import { useStatusActionPipelineContextNew } from '../../../context/StatusActionPipelineContextNew';
import { candidateInteractionMessagingPipelines } from '../../../features/crmSettings/ManageRecruiterActions/helper';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { getMappingFailedNotification, saveMapping, updateMapping } from '../utils/saveMapping';

import ButtonGroup from './ButtonGroup';
import CVViewModal from './CVViewModal';
import WidgetJobManagement from './WidgetJobManagement';

interface IMappingFormNew {
  state: IConversationState;
  recruiterAction: string | null;
  selectedJob?: IJob | null;
  closeDrawer?: (args?: ICandidate) => void;
  mappingFormSettings?: IMappingFormSettingsResponse;
  isLayout?: boolean;
  closeMap?: () => void;
  mappingPurpose: string;
}
// interface Node {
//   id: string;
//   name: string;
//   nodes: Node[];
// }

interface IMappingFormState {
  resumeURl: string | null;
  openCvView: boolean;
  candidateInfo: ICandidate | null;
  candidateResumes: ICandidateResume[];
  disableSubmit: boolean;
  results: string[];
  actions: string[];
  jobForUpdate: IJob | null;
}

const DrawerFooter = styled(Grid)(() => ({
  position: 'fixed',
  bottom: 1,
  justifyContent: 'revert',
  background: WsColors.White,
  width: '53%',
}));

const MappingFormNew: FC<IMappingFormNew> = ({
  state,
  recruiterAction,
  selectedJob,
  mappingFormSettings,
  isLayout,
  closeMap,
  mappingPurpose = 'map',
}) => {
  const refSubmitButtom = useRef<HTMLButtonElement>(null);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const activityConversationFor = useConversationWidgetStore(
    (state) => state.activityConversationFor
  );
  const [formState, setFormState] = useState<IMappingFormState>({
    resumeURl: null,
    openCvView: false,
    candidateInfo: activityConversation ?? state.candidate,
    candidateResumes: [],
    disableSubmit: false,
    results: [],
    actions: [],
    jobForUpdate: null,
  });
  const pipelineData: PipelineResultCommSettings = useStatusActionPipelineContextNew();

  const onCandidateUpdate = useConversationWidgetStore((state) => state.onCandidateUpdate);
  // const footerAction = useConversationWidgetStore((state) => state.footerAction);
  const getAllNames = (data: any[]): string[] => {
    const extractNames = (nodes: any[]): string[] =>
      nodes.reduce((names: string[], node: any) => {
        names.push(node.name);
        if (node.nodes && node.nodes.length > 0) {
          names.push(...extractNames(node.nodes));
        }
        return names;
      }, []);

    return data.reduce((allNames: string[], item: any) => {
      allNames.push(item.name);
      if (item.nodes && item.nodes.length > 0) {
        allNames.push(...extractNames(item.nodes));
      }
      return allNames;
    }, []);
  };

  const [actionsData, setActionsData] = useState<candidateInteractionMessagingPipelines[]>([]);
  const [communicationData, setCommunicationData] = useState<
    candidateInteractionMessagingPipelines[]
  >([]);
  const [resultData, setResultData] = useState<candidateInteractionMessagingPipelines[]>([]);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedComms, setSelectedComms] = useState<string[]>([]);
  const [selectedResults, setSelectedResults] = useState<string[]>([]);
  const [called, setCalled] = useState('');
  const [suitable, setSuitable] = useState('');
  const [respone, setResponse] = useState('');

  useEffect(() => {
    if (!pipelineData) return;
    const processPipelineData = (pipeline: PipelineResultCommSettings) => {
      setActionsData(pipeline.pipelineData ?? []);
      setCommunicationData(pipeline.communicationData ?? []);
      setResultData(pipeline.resultData ?? []);
    };

    if (pipelineData) {
      processPipelineData(pipelineData);
    }
  }, [pipelineData]);

  useEffect(() => {
    if (!pipelineData) return;
    const processPipelineData = () => {
      setSelectedActions(
        getAllNames(actionsData).filter((e) => formState.actions.includes(e)) ?? []
      );
      setSelectedComms(
        getAllNames(communicationData).filter((e) => formState.actions.includes(e)) ?? []
      );
      setSelectedResults(formState.results ?? []);
    };
    processPipelineData();
  }, [
    actionsData,
    communicationData,
    formState.actions,
    formState.results,
    pipelineData,
    mappingPurpose,
  ]);

  const setTempConv = useConversationWidgetStore((state) => state.setTempConv);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const mapBeforeNavigation = useConversationWidgetStore((state) => state.mapBeforeNavigation);
  const candidate = activityConversation ?? state.candidate;
  const contactActionObject = !isEmpty(candidate?.contactActionsObjs)
    ? candidate?.contactActionsObjs
    : null;
  const contactActions =
    contactActionObject && contactActionObject[contactActionObject.length - 1].actions;
  const contactResults =
    contactActionObject && contactActionObject[contactActionObject.length - 1].result.result;
  const setNotification = useNotificationStore((state) => state.setNotification);
  const disableMapping = !activityConversation
    ? candidate?.jobId &&
      candidate.jobId === selectedJob?.id &&
      candidate?.isJobCandidateMapping === false
    : false;
  // const formOptions = {
  //   resolver: yupResolver(
  //     mappingFormSchema(mappingFormSettings, candidate?.profileURLs?.[0] ?? '')
  //   ),
  //   defaultValues: submissionFormDefaultValues(candidate, selectedJob),
  // };
  const setIsMappingFormChanged = useConversationWidgetStore(
    (state) => state.setIsMappingFormChanged
  );
  const isMappingFormChanged = useConversationWidgetStore((state) => state.isMappingFormChanged);

  useEffect(() => {
    contactActions && setFormState((m) => ({ ...m, actions: contactActions }));
    contactResults && setFormState((m) => ({ ...m, results: contactResults as string[] }));
  }, [contactActions, contactResults]);

  const methods = useForm();

  // useEffect(() => {
  //   setSelectedActions(
  //     isEmpty(activityConversation) && recruiterAction && footerAction !== 'Not Sure'
  //       ? ['CV Review', recruiterAction]
  //       : !isEmpty(activityConversation)
  //       ? formState.actions
  //       : []
  //   );
  //   setSelectedResults(
  //     isEmpty(activityConversation) && footerAction === 'Not Sure'
  //       ? ['Not Sure']
  //       : !isEmpty(activityConversation)
  //       ? formState.results
  //       : []
  //   );
  // }, [activityConversation, footerAction, formState.actions, formState.results, recruiterAction]);
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    const jobCode = activityConversation?.jobCode;
    if (jobCode) {
      (async () => {
        const response = await getJobsFromAdvancedQuery({
          jobCodes: [jobCode],
          page: 0,
          size: 2,
          sort: ['scoringData.score,desc'],
          searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
          jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
          jobStatus: 'All',
        });

        if (!isEmpty(response.data) && response.data[0])
          setFormState((m) => ({ ...m, jobForUpdate: response.data[0] }));

        if (isEmpty(response.data)) {
          try {
            const jobResponse = await getJobByJobCode(jobCode);
            setFormState((m) => ({ ...m, jobForUpdate: jobResponse as IJob }));
          } catch (e) {
            console.log(e);
          }
        }
      })();
    }
  }, [activityConversation]);

  useEffect(() => {
    if (isMappingFormChanged == false && isDirty) setIsMappingFormChanged(true);
  }, [isDirty, isMappingFormChanged, setIsMappingFormChanged]);

  const onError = () => {
    setNotification(warning('Please enter required fields'));
  };
  const updateMap = useCallback(
    async (data: FieldValues) => {
      try {
        if (isLayout) closeMap && closeMap();

        const response =
          formState.candidateInfo && (await updateMapping(data, formState.candidateInfo));
        if (response && response?.status != 'fail') {
          await setNotification(success('Mapping Updated'));
          updateFromPage('mapping');
          setIsMappingFormChanged(false);
          if (!isLayout) {
            mapBeforeNavigation ? setTempConv(response) : onCandidateUpdate(response);
          }
        } else {
          setFormState((m) => ({ ...m, disableSubmit: false }));
          setNotification(failed(getMappingFailedNotification(response?.response)));
        }
      } catch (error) {
        setNotification(failed('Mapping failed'));
      }
    },
    [
      closeMap,
      formState.candidateInfo,
      mapBeforeNavigation,
      onCandidateUpdate,
      setIsMappingFormChanged,
      setNotification,
      setTempConv,
      updateFromPage,
      mappingPurpose,
    ]
  );

  const onFormSubmit = useCallback(
    async (data: FieldValues) => {
      if (isLayout) {
        closeMap && closeMap();
      }
      data.downloadSource = candidate.downloadSource ?? 'NOT_SPECIFIED';
      const actns = selectedActions.concat(selectedComms.concat([called, suitable])) ?? [];
      const actionsWithoutEmptyStrings = actns.filter((action) => action.trim() !== '');
      const resultsWithoutEmptyStrings = selectedResults.filter((result) => result.trim() !== '');

      data.recruiterActivity = {
        actions: Array.from(new Set(actionsWithoutEmptyStrings)) ?? [],
        results: Array.from(new Set(resultsWithoutEmptyStrings)) ?? [],
      };

      setFormState((m) => ({ ...m, disableSubmit: true }));
      if (activityConversation && formState.candidateInfo && selectedJob) {
        updateMap(data);
      } else if (formState.candidateInfo && selectedJob && selectedJob.id) {
        try {
          const response = await saveMapping(data, formState.candidateInfo, selectedJob);
          if (response && response.status == 'success') {
            await setNotification(success('Candidate is mapped to selected job'));
            window.location.reload();
            updateFromPage('mapping');
            setIsMappingFormChanged(false);
            if (!isLayout) {
              mapBeforeNavigation
                ? setTempConv(response.response)
                : onCandidateUpdate(response.response);
            }
          } else {
            response?.response.indexOf('There is no accepted work assignment for the job') > -1
              ? setNotification(
                  failed(
                    'This assigned job is not been accepted by the recruiter. Please accept the job then map the candidate.'
                  )
                )
              : setNotification(failed('Mapping failed'));
            setFormState((m) => ({ ...m, disableSubmit: false }));
          }
        } catch (e) {
          setNotification(failed('Mapping failed'));
        }
      } else if (isEmpty(recruiterAction) && selectedJob && selectedJob.id) {
        updateMap(data);
      } else if (isEmpty(selectedJob)) {
        setFormState((m) => ({ ...m, disableSubmit: false }));
        setNotification(warning('Please select job first'));
      } else {
        setNotification(failed('Mapping failed'));
      }
    },
    [
      activityConversation,
      called,
      candidate.downloadSource,
      formState.candidateInfo,
      mapBeforeNavigation,
      onCandidateUpdate,
      recruiterAction,
      selectedActions,
      selectedComms,
      selectedJob,
      selectedResults,
      setIsMappingFormChanged,
      setNotification,
      setTempConv,
      suitable,
      updateFromPage,
      updateMap,
    ]
  );

  useEffect(() => {
    if (mapBeforeNavigation) {
      refSubmitButtom?.current?.click();
    }
  }, [handleSubmit, mapBeforeNavigation, onFormSubmit]);
  const [action, setActions] = useState('');
  const [communication, setCommunication] = useState('');
  const [action1, setActions1] = useState('');
  const [communication1, setCommunication1] = useState('');
  const [result, setResult] = useState('');
  const [result1, setResult1] = useState('');

  const getButtonsList = (
    messagingPipelineData: candidateInteractionMessagingPipelines[],
    action: string
  ): candidateInteractionMessagingPipelines[] =>
    messagingPipelineData.flatMap((m) => (m.name === action ? m.nodes.map((node) => node) : []));
  const getButtonsList1 = (
    messagingPipelineData: candidateInteractionMessagingPipelines[],
    action: string,
    action1: string
  ): candidateInteractionMessagingPipelines[] =>
    messagingPipelineData.flatMap((m) =>
      m.name === action
        ? m.nodes.flatMap((node) =>
            node.name === action1 && node.nodes
              ? node.nodes.flatMap((nestedNode) => nestedNode)
              : []
          )
        : []
    );

  const findMissingElements = (first, second, parent, optional?: string) => {
    const missingElements: string[] = [];
    first.forEach((element) => {
      if (!second.includes(element)) {
        missingElements.push(element);
      }
    });
    if (missingElements.length > 0) {
      const a = missingElements?.[0];
      let arr: string[] = [];

      if (optional) {
        arr = getNestedNames(parent, optional, a);
        console.log(arr, optional, a);
      } else {
        arr = getNames(parent, a) ?? [];
      }
      const newArray = second.filter((element) => !arr.includes(element));
      return newArray;
    } else {
      return second;
    }
  };
  const reset = () => {
    setSelectedResults(formState.results ?? []);
    setSelectedActions(getAllNames(actionsData).filter((e) => formState.actions.includes(e)) ?? []);
    setSelectedComms(
      getAllNames(communicationData).filter((e) => formState.actions.includes(e)) ?? []
    );
  };

  const getNames = (data: candidateInteractionMessagingPipelines[], name: string): string[] =>
    data.flatMap((m) => (m.name === name ? m.nodes.map((node) => node.name) : []));
  const getNestedNames = (
    messagingPipelineData: candidateInteractionMessagingPipelines[],
    action: string,
    action1: string
  ): string[] =>
    messagingPipelineData.flatMap((m) =>
      m.name === action
        ? m.nodes.flatMap((node) =>
            node.name === action1 && node.nodes
              ? node.nodes.flatMap((nestedNode) => nestedNode.name)
              : []
          )
        : []
    );

  return (
    <FormProvider {...methods}>
      {!disableMapping && activityConversationFor !== 'submission' ? (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <form
          onSubmit={handleSubmit(onFormSubmit, onError)}
          onKeyDown={(e) => e.code === 'Enter' && e.preventDefault()}
        >
          <Grid
            xs={12}
            item
            container
            p={1.5}
            sx={{ border: `1px solid ${WsColors.TertiaryShades100}` }}
          >
            <Grid xs={7} pl={1}>
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold={true}>
                Map Conversation
              </WsTypography>
            </Grid>
            <Grid xs={5} display="flex" justifyContent="flex-end" pl={3}>
              <WidgetJobManagement
                selectedJob={
                  activityConversation
                    ? formState.jobForUpdate
                      ? formState.jobForUpdate
                      : null
                    : selectedJob
                }
                jobFetchFailure={!isEmpty(activityConversation) && isEmpty(formState.jobForUpdate)}
                ss={12}
                hideRemove={true}
              />
            </Grid>
          </Grid>
          <Grid
            sx={{
              background: WsColors.TertiaryShades50,
              overflowY: 'auto',
              maxHeight: '90vh',
              minHeight: '85vh',
              pb: 3,
            }}
            px={2}
          >
            <ButtonGroup
              inputTitle="Actions"
              selectedColor={WsColors.TheBlues600}
              buttonsList={actionsData.map((m) => m)}
              size="large"
              multiple={actionsData?.[0]?.condition === 'or' ? false : true}
              selectedIndexValue={(index, v) => {
                setActions(v);
                setActions1('');
                const newArray = selectedActions.filter(
                  (element) => !getNames(actionsData, action).includes(element)
                );
                setSelectedActions(newArray);
                if (isEmpty(v)) {
                  setSelectedActions([]);
                } else if (selectedActions.indexOf(v) == -1) {
                  setSelectedActions([v]);
                }
              }}
              selectedMultipleIndexValue={(index, v) => {
                if (isEmpty(v)) {
                  setSelectedActions([]);
                }
              }}
              actions={selectedActions}
              leftChild={
                <WsButton variant="text" onClick={reset}>
                  {'Reset'}
                </WsButton>
              }
            />
            {getButtonsList(actionsData, action).length > 0 && (
              <Box pl={2}>
                <ButtonGroup
                  inputTitle={action}
                  selectedColor={WsColors.TheBlues500}
                  buttonsList={getButtonsList(actionsData, action)}
                  size="medium"
                  multiple={
                    getButtonsList(actionsData, action)?.[0]?.condition === 'or' ? false : true
                  }
                  selectedIndexValue={(index, v) => {
                    if (getButtonsList(actionsData, action)?.[0]?.condition === 'or') {
                      if (suitable)
                        setSelectedActions(
                          findMissingElements(
                            selectedActions,
                            [v].concat(actionsData.map((m) => m.name)),
                            actionsData,
                            action
                          )
                        );
                      setActions1(v);
                      setSuitable(v);
                    } else {
                      setActions1(v);
                    }
                  }}
                  selectedMultipleIndexValue={(index, v) => {
                    if (getButtonsList(actionsData, action)?.[0]?.condition !== 'or') {
                      setSelectedActions(
                        findMissingElements(selectedActions, v, actionsData, action)
                      );
                    }
                  }}
                  actions={selectedActions}
                />
                {getButtonsList1(actionsData, action, action1).length > 0 && (
                  <Box pl={2}>
                    <ButtonGroup
                      inputTitle={action1}
                      selectedColor={WsColors.TheBlues500}
                      buttonsList={getButtonsList1(actionsData, action, action1)}
                      size="medium"
                      multiple={true}
                      selectedMultipleIndexValue={(index, v) => setSelectedActions(v)}
                      actions={selectedActions}
                      selectedIndexValue={(index, v) => {
                        if (actionsData?.[0]?.condition === 'or')
                          setSelectedActions(selectedActions.concat([v]));
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Divider sx={{ color: WsColors.TertiaryShades800, height: 1, p: 1 }} />
            <ButtonGroup
              inputTitle="Communication"
              buttonsList={communicationData.map((m) => m)}
              size="large"
              multiple={communicationData?.[0]?.condition === 'or' ? false : true}
              selectedIndexValue={(index, v) => {
                setCommunication(v);
                setCommunication1('');
                const newArray = selectedComms.filter(
                  (element) => !getNames(communicationData, communication).includes(element)
                );
                setSelectedComms(newArray);
                if (isEmpty(v)) {
                  setSelectedComms([]);
                } else if (selectedComms.indexOf(v) == -1) {
                  setSelectedComms([v]);
                }
              }}
              selectedMultipleIndexValue={(index, v) => {
                if (isEmpty(v)) {
                  setSelectedComms([]);
                }
              }}
              actions={selectedComms}
            />
            {getButtonsList(communicationData, communication).length > 0 && (
              <Box pl={2}>
                <ButtonGroup
                  inputTitle={communication}
                  selectedColor={WsColors.TheBlues500}
                  buttonsList={getButtonsList(communicationData, communication)}
                  size="medium"
                  multiple={
                    getButtonsList(communicationData, communication)?.[0]?.condition === 'or'
                      ? false
                      : true
                  }
                  selectedIndexValue={(index, v) => {
                    if (getButtonsList(communicationData, communication)?.[0]?.condition === 'or') {
                      if (called)
                        setSelectedComms(
                          findMissingElements(
                            selectedComms,
                            [v].concat(communicationData.map((m) => m.name)),
                            communicationData,
                            communication
                          )
                        );
                      setCommunication1(v);
                      setCalled(v);
                    } else {
                      setCommunication1(v);
                    }
                  }}
                  selectedMultipleIndexValue={(index, v) => {
                    if (getButtonsList(communicationData, communication)?.[0]?.condition !== 'or') {
                      setSelectedComms(
                        findMissingElements(selectedComms, v, communicationData, communication)
                      );
                    }
                  }}
                  actions={selectedComms}
                />
                {getButtonsList1(communicationData, communication, communication1).length > 0 && (
                  <Box pl={2}>
                    <ButtonGroup
                      inputTitle={action1}
                      selectedColor={WsColors.TheBlues500}
                      buttonsList={getButtonsList1(
                        communicationData,
                        communication,
                        communication1
                      )}
                      size="medium"
                      multiple={
                        getButtonsList1(communicationData, communication, communication1)?.[0]
                          ?.condition === 'or'
                          ? false
                          : true
                      }
                      selectedMultipleIndexValue={(index, v) => setSelectedComms(v)}
                      selectedIndexValue={(index, v) => {
                        if (communicationData?.[0]?.condition === 'or')
                          setSelectedComms(selectedComms.concat([v]));
                      }}
                      actions={selectedComms}
                    />
                  </Box>
                )}
              </Box>
            )}

            <Divider sx={{ color: WsColors.TertiaryShades800, height: 1, p: 1 }} />

            <ButtonGroup
              inputTitle="Candidate Response"
              buttonsList={resultData.map((m) => m)}
              size="large"
              multiple={resultData?.[0]?.condition === 'or' ? false : true}
              selectedIndexValue={(index, v) => {
                setResult(v);
                setResult1('');
                const newArray = selectedResults.filter(
                  (element) => !getNames(resultData, result).includes(element)
                );
                setSelectedResults(newArray);
                if (isEmpty(v)) {
                  setSelectedResults([]);
                } else if (selectedResults.indexOf(v) == -1) {
                  setSelectedResults([v]);
                }
              }}
              selectedMultipleIndexValue={(index, v) => {
                if (isEmpty(v)) {
                  setSelectedResults([]);
                }
              }}
              actions={selectedResults ?? []}
            />

            {getButtonsList(resultData, result).length > 0 && (
              <Box pl={2}>
                <ButtonGroup
                  inputTitle={result}
                  selectedColor={WsColors.TheBlues500}
                  buttonsList={getButtonsList(resultData, result)}
                  size="medium"
                  multiple={
                    getButtonsList(resultData, result)?.[0]?.condition === 'or' ? false : true
                  }
                  selectedIndexValue={(index, v) => {
                    if (getButtonsList(resultData, result)?.[0]?.condition === 'or') {
                      if (respone)
                        setSelectedResults(
                          findMissingElements(
                            selectedResults,
                            [v].concat(resultData.map((m) => m.name)),
                            resultData,
                            result
                          )
                        );
                      setResult1(v);
                      setResponse(v);
                    } else {
                      setResult1(v);
                    }
                  }}
                  selectedMultipleIndexValue={(index, v) => {
                    if (getButtonsList(resultData, result)?.[0]?.condition !== 'or') {
                      setSelectedResults(
                        findMissingElements(selectedResults, v, resultData, result)
                      );
                    }
                  }}
                  actions={selectedResults}
                />
                {getButtonsList1(resultData, result, result1).length > 0 && (
                  <Box pl={2}>
                    <ButtonGroup
                      inputTitle={result1}
                      selectedColor={WsColors.TheBlues500}
                      buttonsList={getButtonsList1(resultData, result, result1)}
                      size="medium"
                      multiple={
                        getButtonsList1(resultData, result, result1)?.[0]?.condition === 'or'
                          ? false
                          : true
                      }
                      selectedMultipleIndexValue={(index, v) => setSelectedResults(v)}
                      selectedIndexValue={(index, v) => {
                        if (resultData?.[0]?.condition === 'or')
                          setSelectedResults(selectedResults.concat([v]));
                      }}
                      actions={selectedResults}
                    />
                  </Box>
                )}
              </Box>
            )}
            {mappingFormSettings?.notes && (
              <Box pt={3}>
                <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK}>
                  Add Comment
                </WsTypography>
                <Controller
                  name="notes"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <TextareaAutosize
                        aria-label="notes"
                        onChange={onChange}
                        minRows={2}
                        value={value}
                        placeholder="Call/Message Notes"
                        style={{ width: '100%', height: '80px', fontFamily: 'Manrope' }}
                      />
                      {error && <FormHelperText error>{error.message}</FormHelperText>}
                    </>
                  )}
                />
              </Box>
            )}
            {candidate.candidateNotes && candidate.candidateNotes?.length > 0 && (
              <Box py={2}>
                <WsTagsList
                  chipArray={candidate.candidateNotes.map((r) => r.notes)}
                  count={3}
                  type={ChipsType.SECONDARY}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <DrawerFooter>
              <Stack p={1} direction="row" justifyContent="flex-end" mr={2}>
                <WsButton
                  type="submit"
                  disabled={formState.disableSubmit}
                  endIcon={
                    formState.disableSubmit ? (
                      <CircularProgress size={17} color="secondary" />
                    ) : undefined
                  }
                >
                  {formState.disableSubmit ? 'Saving...' : activityConversation ? 'Update' : 'Save'}
                </WsButton>
              </Stack>
            </DrawerFooter>
          </Grid>
        </form>
      ) : (
        <Stack direction="column" spacing={2} mt={4} justifyContent="center" alignItems="center">
          <Item>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.ERROR}>
              Duplicate Candidate
            </WsTypography>
            <WsTypography>
              {`This Candidate is already submitted for the job ${
                activityConversationFor === 'submission'
                  ? formState.jobForUpdate?.title
                  : selectedJob?.title
              } 
            (${
              activityConversationFor === 'submission'
                ? formState.jobForUpdate?.jobCode
                : selectedJob?.jobCode
            }). Therefore you cannot map this candidate)`}
            </WsTypography>
          </Item>
        </Stack>
      )}
      {!isEmpty(formState.candidateInfo?.mappedCandidateResumes) && (
        <CVViewModal
          open={formState.openCvView}
          s3url={formState.resumeURl}
          handleClose={() => setFormState((m) => ({ ...m, openCvView: false }))}
        />
      )}
    </FormProvider>
  );
};

export default MappingFormNew;
